import {HubOutlined} from '@mui/icons-material';
import {FC, useState} from 'react';
import {useCan, usePersona} from 'src/permissions/hooks';
import {TabPanel} from 'src/components/common/TabPanel';
import {FormattedMessage, useIntl} from 'react-intl';
import {Box, Tab, Tabs, TabsProps, Typography} from '@mui/material';
import {tagsApi} from 'src/api';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {panelBadgeSx, panelHeaderSx, panelTitleSx, tabSelectedSx, tabsBandProps, tabsBandSx, tabsRootSx, tabSx} from './DriverInformation.sx';
import {RfidTable} from 'src/components/common/RfidTable';
import {DriverInformationProps} from './DriverInformation.types';
import {BexioAccountsTable} from 'src/components/common/BexioAccountsTable';
import {CustomInvoiceItemsTable} from 'src/components/common/CustomInvoiceItemsTable';

export const DriverInformation: FC<DriverInformationProps> = ({userData}) => {
    const driverId = userData?.id ?? 0;


    const persona = usePersona();
    const canSearchRfidTags = useCan('rfidTags.search');
    const intl = useIntl();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();


    // Only fetch tags if the user has permission to read RFID
    const canReadRfid = useCan('rfidTags.search');

    // The permission the endpoints behind this tab check, not a list of who is allowed to hold it.
    // Written as "back office, but not the platform administrator", it hid the tab from the one
    // account that can read every Bexio account there is.
    const canManageBexioAccounts = useCan('bexioAccounts.search');
    // Check if a user can manage custom invoice items (matches backend roles)
    // Was a list of three role names mirroring the backend guard by hand, and it silently dropped
    // the business's own managers, so they lost the control here while the API allowed them.
    const canManageInvoiceItems = useCan('customInvoiceItems.create');

    // Calculate tab indices dynamically based on permissions
    let tabIndex = 0;
    const rfidTabIndex = canReadRfid ? tabIndex++ : -1;
    const bexioTabIndex = canManageBexioAccounts ? tabIndex++ : -1;
    const invoiceItemsTabIndex = canManageInvoiceItems ? tabIndex++ : -1;

    // Get RFID tags for drivers using search API
    const {data: rfidData} = tagsApi.useGetTagsQuery({
        params: {
            page: 0,
            size: 100,
        },
        data: {
            businessId: businessId,
            sortAsc: false,
            sortField: 'CREATED_AT',
            keyword: '',
            driverId: driverId ? driverId : undefined,
        },
    }, {
        skip: !canReadRfid || !driverId, // Skip API call if the user doesn't have permission or no driver ID
    });

    const [activeTab, setActiveTab] = useState<number>(0);

    const handleSetTab: TabsProps['onChange'] = (_event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Box sx={tabsRootSx}>
            <Box sx={panelHeaderSx}>
                <Box sx={panelBadgeSx}>
                    <HubOutlined/>
                </Box>
                <Typography sx={panelTitleSx}>
                    <FormattedMessage id="relationships"/>
                </Typography>
            </Box>
            <Box>
                <Box sx={tabsBandSx}>
                    <Tabs
                        {...tabsBandProps}
                        value={activeTab}
                        onChange={handleSetTab}
                        aria-label="driver tabs"
                    >
                        {canReadRfid && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'rfidsTabHeader'})}
                            />
                        )}
                        {canManageBexioAccounts && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'bexioAccounts'})}
                            />
                        )}
                        {canManageInvoiceItems && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'invoiceItems'})}
                            />
                        )}
                    </Tabs>
                </Box>

                {/* RFIDS TAB */}
                {canSearchRfidTags && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={rfidTabIndex}
                    >
                        <RfidTable
                            data={userData ? {
                                ...userData,
                                ocppTags: rfidData?.content || []
                            } as any : undefined}
                            isCompanyUser={false}
                        />
                    </TabPanel>
                                )}

                {/* BEXIO ACCOUNTS TAB */}
                {canManageBexioAccounts && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={bexioTabIndex}
                    >
                        <BexioAccountsTable
                            data={userData}
                        />
                    </TabPanel>
                )}

                {/* INVOICE ITEMS TAB */}
                {canManageInvoiceItems && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={invoiceItemsTabIndex}
                    >
                        <CustomInvoiceItemsTable
                            userId={driverId}
                            businessId={businessId ?? 0}
                        />
                    </TabPanel>
                )}
            </Box>
        </Box>
    );
};